'use client';

import { useCiCItemLookup } from '@dealerportal/catalog-ui/Hooks/useCiCItemLookup';
import { FormatDate } from '@dealerportal/utilities';
import addDays from 'date-fns/addDays';
import { ProductJsonLd } from 'next-seo';
import { ProductDetails } from './ProductDetails';
import { ProductDetailsClientSideSkeleton } from './ProductDetailsClientSideSkeleton';

type Props = {
  catalogVersionId: string;
  slug: string;
  sku: string;
  primaryUrl?: string;
  isMobileDevice: boolean;
  topSectionContent: React.ReactNode;
  marketingContent: React.ReactNode;
  actonForm: React.ReactNode;
  hideBrandColorSkuTitleInPDP?: boolean;
};

export const ProductDetailsClientSide = (props: Props) => {
  const { slug, sku, primaryUrl } = props;
  const { products, isLoading } = useCiCItemLookup([
    `${slug}-${sku}`.replace(/\-missing-sku/g, ''),
  ]);

  if (isLoading) {
    return <ProductDetailsClientSideSkeleton />;
  }

  // The CiC lookup can resolve with no products (e.g. the catalog version is
  // unavailable). In that case, we redirect to the search results page.
  if (products.length === 0) {
    window.location.replace('/p');
    return <ProductDetailsClientSideSkeleton />;
  }

  const {
    productName,
    brandName,
    colorName,
    UPID,
    swatches,
    description,
    unitPrice,
  } = products[0];

  return (
    <>
      <ProductJsonLd
        scriptId="product-json-ld"
        useAppDir={true}
        productName={productName}
        images={[
          swatches.find((swatch) => swatch?.UPID === UPID)?.imageURL ?? '',
        ]}
        description={description}
        color={colorName}
        manufacturerName={brandName}
        brand={brandName}
        sku={sku}
        productID={UPID}
        gtin="N/A"
        model="N/A"
        url={`${primaryUrl}/d/${slug}/${sku}`}
        offers={[
          {
            price: unitPrice ?? 0,
            priceCurrency: 'USD',
            priceValidUntil: FormatDate(addDays(new Date(), 1), 'yyyy-MM-dd'),
            availability: 'https://schema.org/InStock',
            url: `${primaryUrl}/d/${slug}/${sku}`,
          },
        ]}
      />
      <ProductDetails
        productInfo={products[0]}
        isMobileDevice={props.isMobileDevice}
        topSectionContent={props.topSectionContent}
        marketingContent={props.marketingContent}
        actonForm={props.actonForm}
        hideBrandColorSkuTitleInPDP={props.hideBrandColorSkuTitleInPDP}
      />
    </>
  );
};
